import { capitalize, isArray, isBrowser, isFunction } from "@churchofjesuschrist/maps-utils";
import { BoundingBox, Position } from "@churchofjesuschrist/maps-geojson";
const google = isBrowser && window.google ? window.google : {};
if (isBrowser && !window.google) {
  window.google = google;
}
export default google;
export const verify = () => {
  if (!google.maps) {
    throw new Error("Google Maps has not been loaded.");
  }
};
export const toLatLng = (value, literal = false) => {
  if (value) {
    if (google.maps?.LatLng && value instanceof google.maps.LatLng) {
      return literal ? value.toJSON() : value;
    }
    let position = Position.toJSON(value);
    if (position) {
      if (literal || !google.maps?.LatLng) {
        return {
          lat: position.latitude,
          lng: position.longitude
        };
      } else {
        return new google.maps.LatLng(position.latitude, position.longitude);
      }
    }
  }
  return null;
};
export const toLatLngBounds = (value, literal = false) => {
  if (value) {
    if (google.maps?.LatLngBounds && value instanceof google.maps.LatLngBounds) {
      return literal ? value.toJSON() : value;
    }
    let bbox = BoundingBox.toJSON(value);
    if (bbox) {
      if (literal || !google.maps?.LatLngBounds) {
        return {
          east: bbox.northEast.longitude,
          north: bbox.northEast.latitude,
          south: bbox.southWest.latitude,
          west: bbox.southWest.longitude
        };
      } else {
        return new google.maps.LatLngBounds(toLatLng(bbox.southWest), toLatLng(bbox.northEast));
      }
    }
  }
  return null;
};
export const toPoint = (value, literal = false) => {
  if (value) {
    if (literal || !google.maps?.Point) {
      return {
        x: value.x,
        y: value.y
      };
    } else if (!(value instanceof google.maps.Point)) {
      return google.maps.Point(value.x, value.y);
    } else {
      return value;
    }
  }
  return null;
};
export const toSize = (value, literal = false) => {
  if (value) {
    if (literal || !google.maps?.Size) {
      return {
        width: value.width,
        height: value.height
      };
    } else if (!(value instanceof google.maps.Size)) {
      return google.maps.Size(value.width, value.height);
    } else {
      return value;
    }
  }
  return null;
};
export const isMVCArray = value => {
  return value && google.maps?.MVCArray && value instanceof google.maps.MVCArray;
};
export const getArray = array => {
  return isArray(array) ? array : array.getArray();
};
export const mutators = (...properties) => {
  const methods = {};
  properties.forEach(property => {
    let method = capitalize(property);
    methods["get" + method] = function () {
      return this.get(property);
    };
    methods["set" + method] = function (value) {
      this.set(property, value);
    };
  });
  return methods;
};
export const get = (obj, property) => {
  let getter = "get" + capitalize(property);
  return isFunction(obj[getter]) ? obj[getter]() : obj.get(property);
};
export const mapContainerWidth = map => {
  let width = null;
  if (isBrowser) {
    let style = window.getComputedStyle(map.getDiv());
    width = parseInt(style.width, 10);
  }
  return width;
};
export const mapContainerHeight = map => {
  let height = null;
  if (isBrowser) {
    let style = window.getComputedStyle(map.getDiv());
    height = parseInt(style.height, 10);
  }
  return height;
};
export const mapContainerSize = map => {
  let width = mapContainerWidth(map),
    height = mapContainerHeight(map);
  if (width && height) {
    return new google.maps.Size(width, height);
  } else {
    return null;
  }
};
export const fullLongitudeVisible = (map, reference = null) => {
  let projection = canvasProjection(reference || map),
    worldWidth = projection ? projection.getWorldWidth() : null,
    mapWidth = mapContainerWidth(map);
  return mapWidth !== null && worldWidth !== null && mapWidth >= worldWidth;
};
export const canvasProjection = reference => {
  if (reference) {
    if (reference instanceof google.maps.OverlayView) {
      return reference.getProjection();
    }
    let map = null;
    if (reference instanceof google.maps.Map) {
      map = reference;
    } else if (reference.getMap) {
      map = reference.getMap();
    }
    if (map instanceof google.maps.Map) {
      if (!map.$projection) {
        let projection = new google.maps.OverlayView();
        projection.draw = () => {};
        projection.setMap(map);
        map.$projection = projection;
      }
      return map.$projection.getProjection();
    }
    if (reference.fromContainerPixelToLatLng) {
      return reference;
    }
    if (reference.getProjection) {
      return reference.getProjection();
    }
  }
  return null;
};
export const removeMap = objs => {
  let execute = !!objs;
  if (execute) {
    objs.forEach(obj => {
      obj.setMap(null);
    });
  }
  return execute;
};
export const removeMapControl = (map, control, unlessPosition = null) => {
  let removed = false,
    found = false;
  if (map && control) {
    for (let position in map.controls) {
      let controls = map.controls[position];
      for (let i = controls.getLength() - 1; i >= 0; i--) {
        let current = controls.getAt(i);
        if (current === control) {
          if (position !== unlessPosition) {
            controls.removeAt(i);
            removed = true;
          }
          found = true;
          break;
        }
      }
      if (found) {
        break;
      }
    }
  }
  return removed || !found;
};
export const repositionMapControl = (map, control, position) => {
  if (map && control) {
    let removed = removeMapControl(map, control, position);
    if (removed) {
      map.controls[position].push(control);
    }
  }
};
export const updateMapTypeMap = mapType => {
  let map = mapType.getMap(),
    previousMap = mapType.$map,
    id = mapType.getMapTypeId();
  if (previousMap && map !== previousMap) {
    if (id) {
      if (previousMap.mapTypes.get(id) && !Object.values(google.maps.MapTypeId).includes(id)) {
        previousMap.mapTypes.set(id, null);
      }
      let options = Object.assign({}, previousMap.get("mapTypeControlOptions") || {});
      if (options.mapTypeIds && options.mapTypeIds.includes(id)) {
        options.mapTypeIds = options.mapTypeIds.filter(current => current !== id);
        previousMap.set("mapTypeControlOptions", options);
      }
    }
    delete mapType.$map;
  }
  if (map && id && !mapType.overlay && map.mapTypes.get(id) !== mapType) {
    if (!Object.values(google.maps.MapTypeId).includes(id)) {
      map.mapTypes.set(id, mapType);
    }
    let options = Object.assign({}, map.get("mapTypeControlOptions") || {});
    if (!options.mapTypeIds || !options.mapTypeIds.includes(id)) {
      options.mapTypeIds = [...(options.mapTypeIds || []), id];
      map.set("mapTypeControlOptions", options);
    }
  }
  mapType.$map = map;
};
export const registerSingleClickEventTrigger = obj => {
  let clickTimer = null;
  let clickListener = obj.addListener("click", event => {
    if (!clickTimer) {
      clickTimer = setTimeout(() => {
        clickTimer = null;
        google.maps.event.trigger(obj, "sglclick", event);
      }, 500); // default timeout for windows (TODO: better way to know?)
    }
  });
  let dblClickListener = obj.addListener("dblclick", () => {
    if (clickTimer) {
      clearTimeout(clickTimer);
      clickTimer = null;
    }
  });
  return {
    remove: () => {
      clickListener.remove();
      dblClickListener.remove();
    }
  };
};
export const noPrintClass = "gmnoprint";
export const vertexSize = 9;
export { default as OffsetPoint } from "./components/OffsetPoint";
export { default as OverlayType } from "./components/OverlayType";
export { default as OverlayRegistry } from "./components/OverlayRegistry";